<script>
import { analytics, db, functions,firebaseApp } from '../firebase';
import { mapState } from 'vuex';
import { loadStripe } from '@stripe/stripe-js';

import formatMoney from '@/lib/formatMoney';
import formatDate from '@/mixins/formatDate';
import trialHelpers from '@/mixins/trialHelpers';
import featureEnabled from '@/mixins/featureFlag';
import subsciptionHelpers, {
	isSellableTier,
	highestOwnedTier,
	upgradeSkuFor,
	effectiveTier,
	planRank,
} from '@/mixins/subscriptionHelpers';

// Price IDs can arrive as paths; compare only the last segment.
function normalizePriceId(id) {
	return typeof id === 'string' ? id.split('/').pop() : id;
}

// Marketing copy per tier. First entry is the card description, rest are bullets.
const PARTY_PLAN_FEATURES = {
	'divii_plan': [
		'Your complete self-guided file, from separation to signed Agreement. Invite your spouse at no extra cost.',
		'Parenting time scheduler and holiday planner',
		'Child and spousal support calculations',
		'Property division and equalization',
		'Pension estimation',
		'All education, videos, webinars and live Q&A nights',
		'Share and Collaborate with your Spouse and Lawyer',
		'Agreement Download to PDF or Print',
		'Canadian Cloud Storage with Local Server',
		'Advanced Security & Encryption',
		'Client Support',
		'Fully Drafted Legal Separation Agreement'
	],
	'guided_plan': [
		'Everything in Divii, with a family professional alongside you at the two points that matter most.',
		'Everything included in Divii',
		'An Initial Guidance Session at the start',
		'A professional review of your completed file before your Agreement generates',
		'Fully Drafted Legal Separation Agreement'
	],
	'mediated_plan': [
		'Everything in Divii Guided, plus facilitated online mediation with an accredited family mediator for any terms you cannot agree on.',
		'Everything included in Divii Guided',
		'A private preparation meeting with each of you',
		'Up to two hours of mediation of the unresolved terms',
		'Your Agreement updated to capture everything you resolve',
		'This fee is generally shared between each party'
	]
};

const UPGRADE_TOOLTIPS = {
	'divii_to_guided_upgrade': 'Add an Initial Guidance Session at the start, and a professional review of your completed file before your Agreement generates. You have already paid for Divii, so you pay only the difference.',
	'divii_to_mediated_upgrade': 'Add facilitated online mediation with an accredited family mediator for any terms you cannot agree on: a private preparation meeting with each of you, up to two hours of mediation, and your Agreement updated to capture everything you resolve. You have already paid for Divii, so you pay only the difference.',
	'guided_to_mediated_upgrade': 'Add facilitated online mediation with an accredited family mediator for any terms you cannot agree on. You have already paid for Divii Guided, so you pay only the difference.'
};

// Single-plan "founding rate" lawyer page copy. Gated by feature_lawyer_founding_plan
// (see showFoundingLawyerPlan) so the previous two-plan wording/layout below is left
// completely intact and can be restored just by disabling that flag.
const FOUNDING_LAWYER_PLAN_COPY = {
	eyebrow: 'Divii for Lawyers',
	headline: 'Your first Agreement is done. Keep everything you built.',
	subheadline: 'Your files, your drafts, your client invitations. All of it carries straight into your subscription, uninterrupted.',
	badge: 'Founding rate',
	listPriceLabel: '$179',
	perUserSuffix: 'per user / month',
	rateNote: 'Your founding rate is locked for as long as you stay subscribed.',
	features: [
		'Unlimited files and matters. Your free file was one; this is all of them',
		'Every tool you just used: parenting schedules, support calculators, property distribution, pension estimates',
		'Deterministic Agreement drafting, unlimited regeneration',
		'Client collaboration is free, always. Invite every client to fill in their information form',
		'Clio integration',
	],
	ctaLabel: 'Lock in your founding rate',
	footnote: 'Founding window: first 30 firms · Cancel anytime. Founding rate holds as long as you stay.',
};

// Companion tile shown beside the founding-rate card. Not a purchasable Stripe
// plan — "Contact us" hands off to sales instead of starting a checkout.
const FIRM_PLAN_COPY = {
	badge: 'For growing firms',
	title: 'Firm Plan',
	description: "Increase your firm's profits by equiping all of the family lawyers in your firm with unlimited usage and Premium Listings in our Professional Directory",
	features: [
		{ icon: 'people', title: 'Unlimited usage', subtitle: 'for all family lawyers in your firm' },
		{ icon: 'star', title: 'Premium Listings', subtitle: 'in our Professional Directory' },
		{ icon: 'growth', title: "Support your firm's growth", subtitle: 'with the right tools and visibility' },
	],
	ctaLabel: 'Contact us',
};

export default {
	name: 'Subscription',
	mixins: [formatDate, trialHelpers, featureEnabled, subsciptionHelpers],
	computed: {
		...mapState(['plans', 'plansLoaded', 'user', 'subscriptions', 'currentRoleLevel', 'userProfile']),
		// Check if subscription data is ready to display plans correctly
		isSubscriptionDataReady() {
			// CRITICAL: Wait for BOTH plans AND subscription data to be loaded
			if (!this.plansLoaded) {
				return false; // Plans not fully loaded yet
			}

			if (!this.subscriptionsLoaded) {
				return false; // Subscription data not loaded yet
			}

			// For party users
			if (this.userRoleType === 'party') {
				return true;
			}

			// For lawyer users
			if (this.userRoleType === 'lawyer') {
				return true;
			}

			return true;
		},
		flpPlans() {
			return this.plans.filter(({ role }) => role !== 'assistant_plan');
		},
		assistantPlan() {
			return this.plans.find(({ role }) => role === 'assistant_plan');
		},
		// Get current user's role type
		userRoleType() {
			// Only return 'lawyer' if we have explicit data, otherwise return null to prevent showing lawyer header
			if (this.currentRoleLevel?.roleAssociated) {
				return this.currentRoleLevel.roleAssociated;
			}
			if (this.userProfile?.roleAssociated) {
				return this.userProfile.roleAssociated;
			}
			// Return null instead of defaulting to 'lawyer' to prevent flash
			return null;
		},
		// Filter plans based on user type
		relevantPlans() {
			const roleType = this.userRoleType;
			if (!roleType) {
				// If role not loaded yet, return empty array
				return [];
			}
			const plans = this.plans.filter(plan => {
				const planRole = plan.stripe_metadata_roleAssociated;
				if (roleType === 'party') {
					// Check if plan has party role
					if (planRole !== 'party') {
						return false;
					}
					// Check if plan has stripe_metadata_active_status === 'default' on plan level
					// OR if it has at least one price with stripe_metadata_active_status === 'default'
					const planHasDefaultStatus = plan.stripe_metadata_active_status === 'default';
					const hasDefaultPrice = plan.prices && plan.prices.some(
						price => price.stripe_metadata_active_status === 'default',
					);
					if (!(planHasDefaultStatus || hasDefaultPrice)) {
						return false;
					}

					return isSellableTier(plan.stripe_metadata_plan);
				} else if (roleType === 'lawyer') {
					return planRole === 'lawyer' && plan.role !== 'assistant_plan';
				}
				return false;
			});
			if (roleType === 'party') {
				// Ladder order: cheapest tier on the left.
				plans.sort((a, b) => planRank(a.stripe_metadata_plan) - planRank(b.stripe_metadata_plan));
			}
			return plans;
		},
		// null until payments load, so no card is marked current too early.
		ownedPartyTier() {
			if (this.userRoleType !== 'party' || !this.subscriptionsLoaded) {
				return null;
			}
			const codes = (this.succeededPayments || []).map((payment) => {
				const priceId = normalizePriceId(payment.items?.[0]?.price?.id);
				if (!priceId) {
					return null;
				}
				const plan = this.plans.find((candidate) => {
					return (candidate.prices || []).some(
						(price) => normalizePriceId(price.id) === priceId
					);
				});
				return plan?.stripe_metadata_plan || null;
			});
			return highestOwnedTier(codes);
		},
		// Read from Stripe so the headline price cannot drift from what is charged.
		startingAtLabel() {
			if (this.userRoleType !== 'party' || this.ownedPartyTier) {
				return null;
			}
			const amounts = this.relevantPlans
				.map((plan) => this.getPriceAmount(plan))
				.filter((amount) => amount !== null);
			if (amounts.length === 0) {
				return null;
			}
			return `CA$${formatMoney(Math.min(...amounts) / 100)}`;
		},
		// --- Single "founding rate" lawyer plan (feature_lawyer_founding_plan) ---
		// Additive: does not read from or affect relevantPlans/flpPlans/ownedPartyTier above.
		// Disabling the flag restores the previous two-plan lawyer view untouched.
		showFoundingLawyerPlan() {
			return this.userRoleType === 'lawyer' && this.featureEnabled('feature_lawyer_founding_plan');
		},
		// activeSubscription (subscriptionHelpers mixin) reads the customer's `subscriptions`
		// sub-collection, which this page already used for the old "current plan" highlight.
		// hasActiveSubscription (trialHelpers/subscriptionHelpers mixins) reads
		// userProfile.subscriptionStatus, the field router.js/Profile.vue actually gate access
		// on. The two sources have drifted apart for some existing lawyers, so relying on
		// activeSubscription alone here showed real subscribers the "$179, subscribe now" wall
		// instead of "Current Plan"/"Manage Subscription". OR-ing both together only ever
		// widens who counts as already-subscribed, so it cannot newly hide the founding-rate
		// CTA from anyone who genuinely doesn't have a subscription.
		hasLawyerSubscription() {
			return !!(this.activeSubscription || this.hasActiveSubscription);
		},
		// Reuses the same role==='unlimited' convention already used in getLawyerFeatures/isPlanActive
		// to identify the Growth-tier lawyer product; no new Stripe metadata required.
		foundingLawyerPlan() {
			return this.plans.find(
				(plan) => plan.stripe_metadata_roleAssociated === 'lawyer' && plan.role === 'unlimited'
			) || null;
		},
		foundingPlanCopy() {
			return FOUNDING_LAWYER_PLAN_COPY;
		},
		firmPlanCopy() {
			return FIRM_PLAN_COPY;
		},
		// Raw cents for the founding plan's default price, so the total can be multiplied by seat count.
		// Reuses the existing getDefaultPrice() helper unchanged.
		foundingUnitAmountCents() {
			if (!this.foundingLawyerPlan) {
				return null;
			}
			const interval = this.billingPeriod === 'yearly' ? 'year' : 'month';
			const price = this.getDefaultPrice(this.foundingLawyerPlan, interval);
			return price ? price.unit_amount : null;
		},
		foundingUnitPriceLabel() {
			return this.foundingUnitAmountCents !== null ? formatMoney(this.foundingUnitAmountCents / 100) : '';
		},
		foundingTotalPriceLabel() {
			if (this.foundingUnitAmountCents === null) {
				return '';
			}
			const seats = this.foundingSeatCount > 0 ? this.foundingSeatCount : 1;
			return formatMoney((this.foundingUnitAmountCents / 100) * seats);
		},
	},
	data() {
		return {
			loading: false,
			plansInitiallyLoading: true,
			selectedPlanId: null,
			billingPeriod: 'monthly', // 'monthly' or 'yearly'
			roleLoaded: false,
			succeededPayments: [], // Store succeeded payments from payments collection for party plans
			paymentsUnsubscribe: null, // Firestore listener unsubscribe function
			subscriptionsLoaded: false, // Track if subscription data has been loaded
			foundingSeatCount: 1, // Seat quantity for the new single "founding rate" lawyer plan
			hubspotScriptPromise: null, // Shared load of the HubSpot embed script
			hubspotFormReady: false, // False while the HubSpot form is rendering; drives the modal spinner
		};
	},
	watch: {
		plans() {
			// Only auto-load subscriptions if plans are fully loaded
			if (this.plansLoaded && this.user && this.userRoleType === 'lawyer') {
				const hasSubscriptions = this.subscriptions !== undefined &&
				                       this.subscriptions !== null &&
				                       Array.isArray(this.subscriptions);
				if (!hasSubscriptions) {
					this.$store.dispatch('getSubscriptions', this.user).then(() => {
						this.subscriptionsLoaded = true;
					});
				} else {
					this.subscriptionsLoaded = true;
				}
			}
		},
		userProfile() {
			if (this.userProfile) {
				this.roleLoaded = true;
			}
		},
		currentRoleLevel() {
			if (this.currentRoleLevel) {
				this.roleLoaded = true;
			}
		},
		user(newUser) {
			if (newUser && this.userRoleType === 'party') {
				this.getSucceededPayments().then(() => {
					this.subscriptionsLoaded = true;
				});
			} else if (newUser && this.userRoleType === 'lawyer') {
				const hasSubscriptions = this.subscriptions !== undefined && 
				                       this.subscriptions !== null && 
				                       Array.isArray(this.subscriptions);
				if (!hasSubscriptions) {
					this.$store.dispatch('getSubscriptions', this.user).then(() => {
						this.subscriptionsLoaded = true;
					});
				} else {
					this.subscriptionsLoaded = true;
				}
			}
		},
		// Watch for route changes to refetch payments when navigating back to subscription page
		'$route'(to, from) {
			// Refetch when navigating to subscription page from another route
			const isSubscriptionRoute = to.name === 'Subscription' || to.path.includes('/subscription');
			if (isSubscriptionRoute && from.name && this.user && this.userRoleType === 'party') {
				// Check if returning from checkout
				const isReturningFromCheckout = to.query.session_id || 
				                                to.query.payment_intent ||
				                                sessionStorage.getItem('returningFromCheckout') === 'true';
				
				if (isReturningFromCheckout) {
					// Fetch immediately
					this.getSucceededPayments();
					sessionStorage.removeItem('returningFromCheckout');
				} else {
					// For normal navigation, use minimal delay
					setTimeout(() => {
						this.getSucceededPayments();
					}, 100);
				}
			}
		},
	},
	async mounted() {
		// Warm the HubSpot embed script up front so "Contact us" doesn't wait on it.
		this.loadHubspotScript();
		this.plansInitiallyLoading = true;
		await this.getPlans();
		// Check if role is already available
		if (this.userProfile || this.currentRoleLevel) {
			this.roleLoaded = true;
		}
		await this.$store.dispatch('fetchCurrentCustomerProfile').then(() => {
			this.roleLoaded = true;
		}).catch(() => {
			// If profile fetch fails, still set roleLoaded if we have data
			if (this.userProfile || this.currentRoleLevel) {
				this.roleLoaded = true;
			}
		});
		
		// Check if returning from checkout (Stripe redirects to origin)
		const isReturningFromCheckout = this.$route.query.session_id || 
		                                this.$route.query.payment_intent ||
		                                sessionStorage.getItem('returningFromCheckout') === 'true';
		
		// Load subscription data based on user type
		if (this.user) {
			if (this.userRoleType === 'party') {
				// For party plans, always fetch succeeded payments to get latest data
				// If returning from checkout, fetch immediately without delay
				if (isReturningFromCheckout) {
					// Clear the flag
					sessionStorage.removeItem('returningFromCheckout');
					// Fetch immediately - Firestore should have the data by now
					await this.getSucceededPayments();
					await this.$nextTick();
					this.subscriptionsLoaded = true;
				} else {
					// Normal load
					await this.getSucceededPayments();
					// Wait for Vue to process the reactive update
					await this.$nextTick();
					// Mark as loaded after data is fetched (even if empty array - means no subscriptions)
					this.subscriptionsLoaded = true;
				}
			} else if (this.userRoleType === 'lawyer') {
				// For lawyer plans, check if subscriptions are already loaded
				// subscriptions could be null, undefined, or an empty array
				const hasSubscriptions = this.subscriptions !== undefined && 
				                       this.subscriptions !== null && 
				                       Array.isArray(this.subscriptions);
				
				if (!hasSubscriptions) {
					// Fetch subscriptions if not already loaded
					await this.$store.dispatch('getSubscriptions', this.user);
				}
				// Mark as loaded whether we fetched or it was already available
				// Even if subscriptions is an empty array, that's valid data (means no subscriptions)
				this.subscriptionsLoaded = true;
			} else {
				// If role not determined yet, set loaded to true to avoid blocking
				this.subscriptionsLoaded = true;
			}
		} else {
			this.subscriptionsLoaded = true;
		}
		
		// Listen for page visibility changes and window focus to refetch payments when returning from checkout
		document.addEventListener('visibilitychange', this.handleVisibilityChange);
		window.addEventListener('focus', this.handleWindowFocus);

		this.plansInitiallyLoading = false;

		analytics.logEvent('screen_view', {
			firebase_screen: 'subscripitons',
			firebase_screen_class: 'Subscription',
		});
	},
	activated() {
		// Refetch payments when component is reactivated (for keep-alive)
		if (this.user && this.userRoleType === 'party') {
			// Check if returning from checkout
			const isReturningFromCheckout = this.$route.query.session_id || 
			                                this.$route.query.payment_intent ||
			                                sessionStorage.getItem('returningFromCheckout') === 'true';
			
			if (isReturningFromCheckout) {
				// Fetch immediately without delay
				this.getSucceededPayments().then(() => {
					this.subscriptionsLoaded = true;
				});
				sessionStorage.removeItem('returningFromCheckout');
			} else {
				// For normal reactivation, use minimal delay
				setTimeout(() => {
					this.getSucceededPayments().then(() => {
						this.subscriptionsLoaded = true;
					});
				}, 100);
			}
		} else if (this.user && this.userRoleType === 'lawyer') {
			const hasSubscriptions = this.subscriptions !== undefined && 
			                       this.subscriptions !== null && 
			                       Array.isArray(this.subscriptions);
			if (!hasSubscriptions) {
				this.$store.dispatch('getSubscriptions', this.user).then(() => {
					this.subscriptionsLoaded = true;
				});
			} else {
				this.subscriptionsLoaded = true;
			}
		}
	},
	beforeDestroy() {
		// Clean up event listeners
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		window.removeEventListener('focus', this.handleWindowFocus);
		
		// Clean up Firestore listener
		if (this.paymentsUnsubscribe) {
			this.paymentsUnsubscribe();
			this.paymentsUnsubscribe = null;
		}
	},
	methods: {
		handleVisibilityChange() {
			// Refetch payments when page becomes visible again (user returns from checkout)
			if (!document.hidden && this.user && this.userRoleType === 'party') {
				// Check if we're returning from checkout - if so, fetch immediately
				// Otherwise use a shorter delay for other visibility changes
				const isReturningFromCheckout = this.$route.query.session_id || 
				                                this.$route.query.payment_intent ||
				                                sessionStorage.getItem('returningFromCheckout') === 'true';
				
				if (isReturningFromCheckout) {
					// Fetch immediately - Firestore should have the data
					this.getSucceededPayments();
					sessionStorage.removeItem('returningFromCheckout');
				} else {
					// For other visibility changes, use a minimal delay
					setTimeout(() => {
						this.getSucceededPayments();
					}, 100);
				}
			}
		},
		handleWindowFocus() {
			// Refetch payments when window regains focus (user returns from checkout)
			if (this.user && this.userRoleType === 'party') {
				// Check if we're returning from checkout - if so, fetch immediately
				const isReturningFromCheckout = this.$route.query.session_id || 
				                                this.$route.query.payment_intent ||
				                                sessionStorage.getItem('returningFromCheckout') === 'true';
				
				if (isReturningFromCheckout) {
					// Fetch immediately - Firestore should have the data
					this.getSucceededPayments();
					sessionStorage.removeItem('returningFromCheckout');
				} else {
					// For other focus events, use a minimal delay
					setTimeout(() => {
						this.getSucceededPayments();
					}, 100);
				}
			}
		},
		async getPlans() {
			await this.$store.dispatch('getPlans');
		},
		async getSucceededPayments() {
			// Fetch succeeded payments from payments collection for separating party users
			// Criteria: status === "succeeded" && role !== 'assistant'
			if (!this.user || !this.user.uid) {
				return Promise.resolve();
			}
			
			// Clean up existing listener if any
			if (this.paymentsUnsubscribe) {
				this.paymentsUnsubscribe();
				this.paymentsUnsubscribe = null;
			}
			
			return new Promise((resolve) => {
				try {
					const paymentsRef = db
						.collection('customers')
						.doc(this.user.uid)
						.collection('payments');
					
					// Set up real-time listener for instant updates
					// Use get() first to get initial data synchronously, then set up listener
					paymentsRef
						.where('status', '==', 'succeeded')
						.get()
						.then((snapshot) => {
							const payments = snapshot.docs.map(doc => {
								const data = doc.data();
								return {
									id: doc.id,
									...data,
									createdAt: doc.createTime || data.createdAt || null // Firestore timestamp
								};
							});
							
							// Filter out assistant plans
							const filteredPayments = payments.filter(payment => {
								return payment.role !== 'assistant';
							});
							
							this.succeededPayments = filteredPayments;
							
							// Set up real-time listener for future updates
							this.paymentsUnsubscribe = paymentsRef
								.where('status', '==', 'succeeded')
								.onSnapshot((snapshot) => {
									// Safety check: ensure component is still mounted
									if (!this.$el) {
										return;
									}
									
									const payments = snapshot.docs.map(doc => {
										const data = doc.data();
										return {
											id: doc.id,
											...data,
											createdAt: doc.createTime || data.createdAt || null // Firestore timestamp
										};
									});
									
									// Filter out assistant plans
									const filteredPayments = payments.filter(payment => {
										return payment.role !== 'assistant';
									});
									
									this.succeededPayments = filteredPayments;
								}, (error) => {
									// Safety check: ensure component is still mounted before handling error
									if (!this.$el) {
										return;
									}
									console.error('[getSucceededPayments] Error in payments listener:', error);
									this.succeededPayments = [];
								});
							
							// Use nextTick to ensure Vue has processed the reactive update
							// This ensures computed properties that depend on succeededPayments are updated
							this.$nextTick(() => {
								resolve();
							});
						})
						.catch((error) => {
							// Safety check: ensure component is still mounted before handling error
							if (!this.$el) {
								resolve();
								return;
							}
							console.error('[getSucceededPayments] Error fetching payments:', error);
							this.succeededPayments = [];
							resolve();
						});
					
				} catch (error) {
					console.error('[getSucceededPayments] Error setting up payments listener:', error);
					this.succeededPayments = [];
					resolve();
				}
			});
		},
		async startSubscription(plan) {
			analytics.logEvent('subscriptions_plan_clicked');
			this.loading = true;
			
			let price;

			// For separating party plans, use default price (no interval)
			if (plan.stripe_metadata_roleAssociated === 'party') {
				price = this.getDefaultPrice(plan);
			} else {
				// For lawyer plans, match the same interval currently shown by getPrice()
				const interval = this.billingPeriod === 'yearly' ? 'year' : 'month';
				price = this.getDefaultPrice(plan, interval);
			}

			// Validate that price exists
			if (!price || !price.id) {
				console.error('No valid price found for plan:', plan.name);
				this.loading = false;
				this.selectedPlanId = null;
				return;
			}

			// Build checkout session data
			const checkoutData = {
				allow_promotion_codes: true,
				price: price.id,
				success_url: window.location.origin,
				cancel_url: window.location.origin,
				tax_rates:
					window.location.hostname.split('.')[0] === 'agreements'
						? [
								'txr_1KjtoMGphw9MlUMTQGwfXGaQ',
								'txr_1KnUEJGphw9MlUMTerBLpNcz',
						  ]
						: [
								'txr_1KhLWaGphw9MlUMTuqJYPB5L',
								'txr_1KnU1KGphw9MlUMTgDKkZxY9',
						  ],
			};

			// For separating party plans, use payment mode (one-time payment)
			if (plan.stripe_metadata_roleAssociated === 'party') {
				checkoutData.mode = 'payment';
			}
			// For lawyer plans, mode defaults to 'subscription'

			const docRef = await db
				.collection('customers')
				.doc(this.user.uid)
				.collection('checkout_sessions')
				.add(checkoutData);
			// Wait for the CheckoutSession to get attached by the extension
			docRef.onSnapshot(async (snap) => {
				const { error, sessionId } = snap.data();
				if (error) {
					// Show an error to your customer and
					// inspect your Cloud Function logs in the Firebase console.
					console.error(`An error occured: ${error.message}`);
					this.loading = false;
					this.selectedPlanId = null;
					return;
				}
				if (sessionId) {
					// Set flag to indicate we're redirecting to checkout
					// This helps detect when user returns from payment
					sessionStorage.setItem('returningFromCheckout', 'true');
					
					// We have a session, let's redirect to Checkout
					// Init Stripe
					const stripe = await loadStripe(
						process.env.VUE_APP_STRIPE_KEY
					);
					stripe.redirectToCheckout({ sessionId });
					// Note: loading and selectedPlanId will be reset when user returns from checkout
				} else {
					// No sessionId yet, but no error - still loading
					// Don't reset loading or selectedPlanId yet
				}
			});
		},
		async customerPortal() {
			analytics.logEvent('subscriptions_customer_portal_clicked');
			const functionRef = functions.httpsCallable(
				'ext-firestore-stripe-subscriptions-createPortalLink'
			);
			const { data } = await functionRef({
				returnUrl: window.location.origin,
			});
			window.location.assign(data.url);
		},
		// Raw cents, so the "starting at" figure can compare amounts.
		getPriceAmount(plan) {
			if (plan.stripe_metadata_roleAssociated !== 'party') {
				return null;
			}
			plan = this.chargePlanFor(plan);
			if (!plan.prices) {
				return null;
			}
			const defaultPrice = plan.prices.find(
				(price) => price.stripe_metadata_active_status === 'default'
			);
			const price = defaultPrice || plan.prices[0];
			return price ? price.unit_amount : null;
		},
		// Checkout for the new single "founding rate" lawyer plan only. Deliberately kept
		// separate from startSubscription() (rather than adding a quantity param to it) so
		// the existing lawyer/party checkout path used by every current subscriber is not
		// touched by this feature at all.
		async startFoundingSubscription() {
			const plan = this.foundingLawyerPlan;
			if (!plan) {
				console.error('[startFoundingSubscription] Founding plan not found');
				return;
			}

			analytics.logEvent('subscriptions_founding_plan_clicked');
			this.loading = true;
			this.selectedPlanId = plan.id;

			const interval = this.billingPeriod === 'yearly' ? 'year' : 'month';
			const price = this.getDefaultPrice(plan, interval);

			if (!price || !price.id) {
				console.error('No valid price found for founding plan:', plan.name);
				this.loading = false;
				this.selectedPlanId = null;
				return;
			}

			const checkoutData = {
				allow_promotion_codes: true,
				price: price.id,
				quantity: this.foundingSeatCount > 0 ? this.foundingSeatCount : 1,
				success_url: window.location.origin,
				cancel_url: window.location.origin,
				tax_rates:
					window.location.hostname.split('.')[0] === 'agreements'
						? [
								'txr_1KjtoMGphw9MlUMTQGwfXGaQ',
								'txr_1KnUEJGphw9MlUMTerBLpNcz',
						  ]
						: [
								'txr_1KhLWaGphw9MlUMTuqJYPB5L',
								'txr_1KnU1KGphw9MlUMTgDKkZxY9',
						  ],
			};

			const docRef = await db
				.collection('customers')
				.doc(this.user.uid)
				.collection('checkout_sessions')
				.add(checkoutData);
			docRef.onSnapshot(async (snap) => {
				const { error, sessionId } = snap.data();
				if (error) {
					console.error(`An error occured: ${error.message}`);
					this.loading = false;
					this.selectedPlanId = null;
					return;
				}
				if (sessionId) {
					sessionStorage.setItem('returningFromCheckout', 'true');
					const stripe = await loadStripe(
						process.env.VUE_APP_STRIPE_KEY
					);
					stripe.redirectToCheckout({ sessionId });
				}
			});
		},
		// Firm Plan is enterprise/sales-assisted, not self-serve checkout — no Stripe price
		// or Checkout Session involved; opens the HubSpot contact form in a modal.
		contactFirmPlanSales() {
			analytics.logEvent('subscriptions_firm_plan_contact_clicked');
			this.$bvModal.show('firm-plan-contact-modal');
		},
		// Injects the HubSpot forms embed script once; repeat calls share the same promise.
		loadHubspotScript() {
			if (window.hbspt) {
				return Promise.resolve();
			}
			if (!this.hubspotScriptPromise) {
				this.hubspotScriptPromise = new Promise((resolve, reject) => {
					const script = document.createElement('script');
					script.src = '//js-na3.hsforms.net/forms/embed/v2.js';
					script.charset = 'utf-8';
					script.onload = resolve;
					script.onerror = reject;
					document.body.appendChild(script);
				});
			}
			return this.hubspotScriptPromise;
		},
		// Renders the HubSpot form into the modal's container div. Bound to the
		// modal's @shown event so the target div already exists (and is visible)
		// by the time hbspt.forms.create runs. BootstrapVue destroys modal content
		// on hide, so the form is re-created on every open; a spinner covers the
		// gap until HubSpot reports the form is ready.
		loadHubspotContactForm() {
			this.hubspotFormReady = false;
			this.loadHubspotScript()
				.then(() => {
					if (!document.querySelector('#firm-plan-contact-form')) {
						return; // Modal was closed before the script finished loading
					}
					window.hbspt.forms.create({
						portalId: '45616986',
						formId: '6926a6cb-268c-4770-8297-7625dcfa5fc7',
						region: 'na3',
						target: '#firm-plan-contact-form',
						onFormReady: () => {
							this.hubspotFormReady = true;
						},
					});
				})
				.catch(() => {
					this.hubspotScriptPromise = null; // Allow a retry on the next open
					this.hubspotFormReady = true;
				});
		},
		// Purely presentational: the founding-plan feature copy is written as
		// "title. detail" / "title: detail" / "title, detail" so it can render as a
		// bold heading + gray sub-line (matches the reference design) without
		// duplicating each feature as a separate title/subtitle pair in the data.
		splitFoundingFeature(feature) {
			const delimiter = ['. ', ': ', ', '].find((sep) => feature.includes(sep));
			if (!delimiter) {
				return { title: feature, subtitle: '' };
			}
			const index = feature.indexOf(delimiter);
			return {
				title: feature.slice(0, index),
				subtitle: feature.slice(index + delimiter.length),
			};
		},
		// Single source of truth for picking a plan's price: always prefer the
		// price flagged stripe_metadata_active_status === 'default'; only fall
		// back to the first matching price when no default is set. Applies the
		// same way to existing plans and any newly created product/price.
		getDefaultPrice(plan, interval = null) {
			if (!plan.prices || plan.prices.length === 0) return null;
			const candidates = interval
				? plan.prices.filter((p) => p.interval == interval)
				: plan.prices;
			if (candidates.length === 0) return null;
			return (
				candidates.find((p) => p.stripe_metadata_active_status === 'default') ||
				candidates[0]
			);
		},
		// Raw cents, so the "starting at" figure can compare amounts.
		getPriceAmount(plan) {
			if (plan.stripe_metadata_roleAssociated !== 'party') {
				return null;
			}
			plan = this.chargePlanFor(plan);
			if (!plan.prices) {
				return null;
			}
			const price = this.getDefaultPrice(plan);
			return price ? price.unit_amount : null;
		},
		getPrice(plan) {
			// For separating party plans, use default price
			if (plan.stripe_metadata_roleAssociated === 'party') {
				const amount = this.getPriceAmount(plan);
				return amount === null ? '' : formatMoney(amount / 100);
			}

			// For lawyer plans, scope candidates to the selected billing interval
			const interval = this.billingPeriod === 'yearly' ? 'year' : 'month';
			const price = this.getDefaultPrice(plan, interval);
			return price ? formatMoney(price.unit_amount / 100) : '';
		},
		isMostPopular(plan) {
			return plan.stripe_metadata_most_popular === 'true';
		},
		upgradeSkuForCard(plan) {
			return upgradeSkuFor(this.ownedPartyTier, plan.stripe_metadata_plan);
		},
		// Charge the upgrade SKU when one covers the jump, else the tier at full price.
		chargePlanFor(plan) {
			const sku = this.upgradeSkuForCard(plan);
			if (!sku) {
				return plan;
			}
			return this.plans.find((p) => p.stripe_metadata_plan === sku) || plan;
		},
		upgradeTooltip(plan) {
			const sku = this.upgradeSkuForCard(plan);
			return sku ? UPGRADE_TOOLTIPS[sku] || null : null;
		},
		planCardState(plan) {
			if (plan.stripe_metadata_roleAssociated !== 'party') {
				return this.isPlanActive(plan) ? 'current' : 'available';
			}
			if (this.isPlanActive(plan)) {
				return 'current';
			}
			const tier = effectiveTier(plan.stripe_metadata_plan);
			if (this.ownedPartyTier && planRank(tier) < planRank(this.ownedPartyTier)) {
				return 'included';
			}
			return 'available';
		},
		planCardLabel(plan) {
			const state = this.planCardState(plan);
			if (state === 'current') {
				return 'Current Plan';
			}
			if (state === 'included') {
				return 'Included';
			}
			return 'Select Plan';
		},
		// Retired-plan customers are handled by hand with Stripe coupons — no in-app path.
		subscribeClick(plan){
			this.selectedPlanId = plan.id;
			if(this.activeSubscription && !this.isPlanActive(plan)){
				this.updateSubscriptionByCustomerPortalLink()
			} else {
				this.startSubscription(this.chargePlanFor(plan));
			}
		},
		async updateSubscriptionByCustomerPortalLink(){
			try {
				this.loading = true;
                const createStripeCustomerPortalLinkFc = firebaseApp.functions('northamerica-northeast1').httpsCallable('createStripeCustomerPortalDashboardLink');
				const reqBody = {
					customerId: this.user.uid,
					success_url: window.location.origin,
					cancel_url: window.location.origin
				}
				const linkResponse = await createStripeCustomerPortalLinkFc(reqBody);
				if(linkResponse?.data?.customerPortalLink) {
					window.location.assign(linkResponse.data.customerPortalLink);
					// Note: loading and selectedPlanId will be reset when user returns from portal
				} else {
					this.loading = false;
					this.selectedPlanId = null;
				}
			} catch (error) {
				console.error(error);
				this.loading = false;
				this.selectedPlanId = null;
			}
		},
		// Get features list based on plan type
		getFeaturesForPlan(plan) {
			const planRole = plan.stripe_metadata_roleAssociated;
			
			if (planRole === 'lawyer') {
				return this.getLawyerFeatures(plan);
			} else if (planRole === 'party') {
				return this.getSeparatingPartyFeatures(plan);
			}
			// Fallback to lawyer features if no metadata
			return this.getLawyerFeatures(plan);
		},
		getLawyerFeatures(plan) {
			const features = [
				'Automated Agreement Builder & Preview',
				'Integrated Client Information Form',
				'AI Assisted Clause Creation',
				'Automated Property Distribution Calculator',
				'Interactive Parenting Schedule',
				'Integrated Editor with automated formatting',
				'Collaborative commenting and tracked changes',
				'Agreement Download to PDF or Print',
				'Integration with Clio',
				'Canadian Cloud Storage with Local Servers',
				'Advanced Security & Encryption'
			];
			
			// Add premium feature for unlimited plan
			if (plan.role === 'unlimited') {
				features.push('Premium Listing in Professional Directory');
			} else {
				features.push(''); // Spacer item
			}
			
			// Add agreement files limit
			const filesLimit = plan.role === 'unlimited' 
				? 'UNLIMITED Agreement Files per Month'
				: '2 Agreement Files per Month';
			features.push(filesLimit);
			
			return features;
		},
		getSeparatingPartyFeatures(plan) {
			// Upgrade SKUs sell the tier they land on.
			const planType = effectiveTier(plan.stripe_metadata_plan);
			const features = PARTY_PLAN_FEATURES[planType];
			if (features) {
				return [...features];
			}
			return [
				'You and your spouse can both use Divii and collaborate on creating a separation agreement',
				'Share and collaborate with your spouse and lawyers',
				'Use of our comprehensive editing tool with track changes and commenting',
				'Client support',
				'Fully Drafted Legal Separation Agreement'
			];
		},
		// Get plan description (first feature for separating party plans)
		getPlanDescription(plan) {
			const features = this.getFeaturesForPlan(plan);
			return features.length > 0 ? features[0] : '';
		},
		// Check if a plan matches the active subscription
		isPlanActive(plan) {
			// Don't show as active until subscription data is loaded
			if (!this.subscriptionsLoaded) {
				return false;
			}
			
			// A party owns a tier, not a price. Matching price IDs here meant an
			// upgrade SKU never matched the tier it bought, so it could be bought twice.
			if (plan.stripe_metadata_roleAssociated === 'party') {
				return effectiveTier(plan.stripe_metadata_plan) === this.ownedPartyTier;
			}

			// For lawyer plans, check active subscription
			if (!this.activeSubscription) return false;
			
			// For lawyer plans, check by product ID (existing logic)
			// Get plan's unique identifier (product ID)
			// The plan.id should be the document ID which is the product ID
			const planProductId = plan.id || plan.product?.id || plan.product;
			
			if (!planProductId) {
				console.warn('[isPlanActive] Plan has no product ID:', plan.name);
				return false;
			}
			
			// Check subscription product in multiple possible locations
			// 1. Direct product field
			if (this.activeSubscription.product) {
				const subscriptionProductId = this.activeSubscription.product?.id 
					|| this.activeSubscription.product 
					|| (typeof this.activeSubscription.product === 'string' 
						? this.activeSubscription.product.split('/').pop() 
						: null);
				if (subscriptionProductId && subscriptionProductId === planProductId) {
					return true;
				}
			}
			
			// 2. Check items[0].plan.product (Stripe subscription structure)
			if (this.activeSubscription.items && this.activeSubscription.items[0]) {
				const itemProductId = this.activeSubscription.items[0].plan?.product
					|| this.activeSubscription.items[0].price?.product;
				if (itemProductId) {
					const subscriptionProductId = typeof itemProductId === 'string' 
						? itemProductId.split('/').pop() 
						: itemProductId;
					if (subscriptionProductId === planProductId) {
						return true;
					}
				}
			}
			
			// 3. Fallback: check role match (for backward compatibility with lawyer plans)
			// Only use role if plan has a unique role value
			if (plan.role && this.activeSubscription.role === plan.role) {
				return true;
			}
			
			return false;
		}
		
	},
};
</script>

<template>
	<div :class="['px-5 container-fluid', userRoleType === 'party' ? 'container-outer-party' : (userRoleType === 'lawyer' ? 'container-outer' : 'container-outer'), { 'founding-plan-page-bg': showFoundingLawyerPlan }]">
		<!-- Firm Plan "Contact us" — embeds the HubSpot contact form instead of
		     navigating away, so the lawyer doesn't lose their place on this page. -->
		<b-modal
			id="firm-plan-contact-modal"
			size="lg"
			hide-footer
			centered
			scrollable
			modal-class="firm-plan-contact-modal"
			header-class="firm-plan-contact-header"
			body-class="firm-plan-contact-body"
			@shown="loadHubspotContactForm"
		>
			<template #modal-title>
				Divii for Lawyers <span class="firm-plan-contact-title-accent">Plans &amp; Pricing</span>
			</template>
			<div class="firm-plan-contact-card">
				<div v-if="!hubspotFormReady" class="firm-plan-contact-loading">
					<div class="spinner-ring"></div>
				</div>
				<div id="firm-plan-contact-form" :class="{ 'firm-plan-contact-form-pending': !hubspotFormReady }"></div>
			</div>
		</b-modal>
		<!-- Hidden when the new founding-rate page is active (it has its own eyebrow/headline);
		     restored automatically for the old two-plan view when the flag is off. -->
		<b-row class="py-5 text-center" v-if="roleLoaded && userRoleType === 'lawyer' && !showFoundingLawyerPlan">
			<b-col>
				<h1 class="mb-5">
					Automate your process. Streamline your practice.
				</h1>
				<p>
					Choose the plan that supports all your clients and watch
					your practice thrive!
				</p>
			</b-col>
		</b-row>
		<b-row class="pb-4 text-center" v-if="roleLoaded && userRoleType === 'party'">
			<b-col>
				<h1 class="party-plans-title">Choose your plan to start your agreement</h1>
				<p class="party-plans-subtitle">One-time payment, no subscription. Your spouse joins free.</p>
			</b-col>
		</b-row>
		<!-- Add loading spinner -->
	<b-row v-if="plansInitiallyLoading" class="loading-container">
		<b-col class="text-center">
			<div class="loading-content" role="status">
				<div class="spinner-wrapper">
					<div class="spinner-ring"></div>
				</div>
				<h3 class="loading-title">Loading plans</h3>
				<p class="loading-subtitle">This only takes a moment.</p>
			</div>
		</b-col>
	</b-row>
		<!--
			New single "founding rate" lawyer plan (feature_lawyer_founding_plan).
			Gated ahead of the existing two-plan row below so that row's markup,
			classes and methods are left completely untouched: turning the flag
			off restores the previous lawyer plan-picker exactly as it was.
		-->
		<b-row
			class="justify-content-md-center"
			v-else-if="(isSubscriptionDataReady || !user) && showFoundingLawyerPlan"
		>
			<b-col sm="auto" class="px-0">
				<div class="founding-plan-section">
					<div class="founding-plan-page text-center">
						<p class="founding-plan-eyebrow">{{ foundingPlanCopy.eyebrow }}</p>
						<h1 class="founding-plan-headline">{{ foundingPlanCopy.headline }}</h1>
						<p class="founding-plan-subheadline">{{ foundingPlanCopy.subheadline }}</p>

						<div class="founding-plan-cards-row">
							<div class="founding-plan-card text-left">
								<div class="founding-plan-badge-row">
									<span :class="['founding-plan-badge', { 'founding-plan-badge-sparkle': !hasLawyerSubscription }]">
										{{ hasLawyerSubscription ? 'Current Plan' : foundingPlanCopy.badge }}
									</span>
									<span v-if="!hasLawyerSubscription" class="founding-plan-list-price">
										{{ foundingPlanCopy.listPriceLabel }} list
									</span>
								</div>

								<div class="founding-plan-price-block">
									<div class="founding-plan-price-row">
										<span class="founding-plan-price">${{ foundingUnitPriceLabel }}</span>
										<span class="founding-plan-price-suffix">{{ foundingPlanCopy.perUserSuffix }}</span>
									</div>
									<p class="founding-plan-rate-note">{{ foundingPlanCopy.rateNote }}</p>
								</div>

								<hr class="founding-plan-divider" />

								<ul class="founding-plan-features">
									<li v-for="(feature, index) in foundingPlanCopy.features" :key="index">
										<span class="founding-plan-check">✓</span>
										<span>
											<span class="founding-plan-feature-title">{{ splitFoundingFeature(feature).title }}</span>
											<span v-if="splitFoundingFeature(feature).subtitle" class="founding-plan-feature-subtitle">{{ splitFoundingFeature(feature).subtitle }}</span>
										</span>
									</li>
								</ul>

								<template v-if="!hasLawyerSubscription">
									<div class="founding-plan-seats-row">
										<span class="founding-plan-seats-label">Users at your firm</span>
										<b-form-input
											type="number"
											min="1"
											v-model.number="foundingSeatCount"
											class="founding-plan-seats-input"
										></b-form-input>
										<span class="founding-plan-seats-total">= ${{ foundingTotalPriceLabel }}/month</span>
									</div>

									<b-button
										class="founding-plan-cta"
										:disabled="loading || !foundingLawyerPlan"
										@click="startFoundingSubscription()"
										block
									>
										<b-spinner small type="grow" v-if="loading"></b-spinner>
										{{ foundingPlanCopy.ctaLabel }}
									</b-button>

									<p class="founding-plan-footnote">{{ foundingPlanCopy.footnote }}</p>
								</template>
								<template v-else>
									<!-- Existing subscriber: manage via the same portal flow already used
									     by subscribeClick()/updateSubscriptionByCustomerPortalLink() today. -->
									<b-button
										class="founding-plan-cta"
										:disabled="loading"
										@click="updateSubscriptionByCustomerPortalLink()"
										block
									>
										<b-spinner small type="grow" v-if="loading"></b-spinner>
										Manage Subscription
									</b-button>
								</template>
							</div>

							<!-- Firm Plan: enterprise contact tile, not a purchasable Stripe plan.
							     Reuses founding-plan-card (same rounded card, tinted for this tile)
							     so it stays visually consistent with the founding-rate card. -->
							<div class="founding-plan-card founding-plan-firm-card text-left">
								<span class="founding-plan-firm-illustration">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="10" height="20"></rect><rect x="14" y="8" width="6" height="14"></rect><line x1="7" y1="6" x2="7" y2="6.01"></line><line x1="11" y1="6" x2="11" y2="6.01"></line><line x1="7" y1="10" x2="7" y2="10.01"></line><line x1="11" y1="10" x2="11" y2="10.01"></line><line x1="7" y1="14" x2="7" y2="14.01"></line><line x1="11" y1="14" x2="11" y2="14.01"></line><line x1="7" y1="18" x2="7" y2="18.01"></line><line x1="11" y1="18" x2="11" y2="18.01"></line><line x1="17" y1="12" x2="17" y2="12.01"></line><line x1="17" y1="16" x2="17" y2="16.01"></line></svg>
								</span>
								<span class="founding-plan-firm-badge">{{ firmPlanCopy.badge }}</span>
								<h2 class="founding-plan-firm-title">{{ firmPlanCopy.title }}</h2>
								<p class="founding-plan-firm-description">{{ firmPlanCopy.description }}</p>

								<ul class="founding-plan-firm-features">
									<li v-for="(feature, index) in firmPlanCopy.features" :key="index">
										<span class="founding-plan-firm-icon">
											<svg v-if="feature.icon === 'people'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"></path><circle cx="10" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
											<svg v-else-if="feature.icon === 'star'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
											<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
										</span>
										<span>
											<span class="founding-plan-feature-title">{{ feature.title }}</span>
											<span class="founding-plan-feature-subtitle">{{ feature.subtitle }}</span>
										</span>
									</li>
								</ul>

								<b-button class="founding-plan-cta founding-plan-cta-outline" block @click="contactFirmPlanSales()">
									{{ firmPlanCopy.ctaLabel }}
								</b-button>
							</div>
						</div>

						<p class="founding-plan-contact-note">
							Firms of 10+ users, <a href="#" class="founding-plan-link" @click.prevent="contactFirmPlanSales()">talk to us</a>
							· A question before you subscribe?<br />
							<a href="mailto:rebecca@divii.ca" class="founding-plan-link founding-plan-link-underline">Email Rebecca directly</a>
						</p>
					</div>
				</div>
			</b-col>
		</b-row>
		<!-- Only show plans when everything is ready -->
		<b-row class="justify-content-md-center" v-else-if="userRoleType && isSubscriptionDataReady">
			<b-col sm="auto">
				<b-card-group deck>
					<b-card
						v-for="plan in (userRoleType === 'party' ? relevantPlans : (relevantPlans.length > 0 ? relevantPlans : flpPlans))"
						:key="plan.id"
						:title="plan.name"
						:class="{ 
							'active-plan': isPlanActive(plan),
							'plan-party': plan.stripe_metadata_roleAssociated === 'party',
							'plan-lawyer': plan.stripe_metadata_roleAssociated === 'lawyer',
							'most-popular': isMostPopular(plan) && plan.stripe_metadata_roleAssociated === 'party'
						}"
						class="plan text-center"
						style="max-width: 420px"
					>
						<!-- Most Popular Badge -->
						<div v-if="isMostPopular(plan) && plan.stripe_metadata_roleAssociated === 'party'" class="most-popular-badge">
							Most Popular
						</div>
						<b-card-text style="flex: 1; display: flex; flex-direction: column;">
							<!-- For separating party plans: show description first, then price -->
							<template v-if="plan.stripe_metadata_roleAssociated === 'party'">
								<p class="text-left mb-4 plan-description">
									{{ getPlanDescription(plan) }}
								</p>
								<div class="price-container-party">
									<span 
										v-if="upgradeTooltip(plan)"
										class="extra-tag"
									>
										extra
									</span>
									<h1 class="price price-party">CA${{ getPrice(plan) }}</h1>
								</div>
								<!-- Button container for party plans - placed directly after price -->
								<div class="button-container after-price">
									<div
										v-b-tooltip.html="{
											title: upgradeTooltip(plan),
											placement: 'bottom',
											boundary: 'viewport',
											triggers: 'hover focus',
											customClass: 'upgrade-card-tooltip-popup'
										}"
										class="d-inline-block w-100 upgrade-card-tooltip"
									>
										<b-button
											:variant="'warning'"
											:class="[
												'btn-party',
												planCardState(plan) === 'current' ? 'btn-current-plan' : ''
											]"
											:disabled="planCardState(plan) !== 'available' || (loading && selectedPlanId === plan.id)"
											@click="subscribeClick(plan)"
											block
										>
											<b-spinner small type="grow" v-if="loading && planCardState(plan) === 'available' && selectedPlanId === plan.id"></b-spinner>
											{{ planCardLabel(plan) }}
										</b-button>
									</div>
								</div>
								<div class="features mt-5 features-party">
									<ul>
										<li 
											v-for="(feature, index) in getFeaturesForPlan(plan).slice(1)" 
											:key="index"
											class="mb-2 feature-item-party"
										>
											{{ feature }}
										</li>
									</ul>
								</div>
							</template>
							<!-- For lawyer plans: show price first, then features -->
							<template v-else>
								<h1 class="price">${{ getPrice(plan) }}</h1>
								<div><small>per month (+ tax)</small></div>
								<div class="mt-3 font-weight-bold">
									Early Bird Price
								</div>
								<!-- <div class="mt-4">
									Valid through to Dec 31, 2022
								</div> -->
								<div class="features mt-5">
									<ul>
										<li 
											v-for="(feature, index) in getFeaturesForPlan(plan)" 
											:key="index"
											:class="{
												'mb-1': true,
												'spacer-item': feature === '',
												'font-weight-bold': feature && (feature.includes('Agreement Files per Month') || feature.includes('UNLIMITED'))
											}"
										>
											<span v-if="feature === ''">&nbsp;</span>
											<span v-else>{{ feature }}</span>
										</li>
									</ul>
								</div>
								<!-- Button for lawyer plans -->
								<div class="button-container">
									<b-button
										variant="primary"
										:class="[
											isPlanActive(plan) ? 'btn-current-plan' : ''
										]"
										:disabled="(loading || isPlanActive(plan)) && selectedPlanId === plan.id"
										@click="subscribeClick(plan)"
										block
									>
										<b-spinner small type="grow" v-if="loading && !isPlanActive(plan) && selectedPlanId === plan.id"></b-spinner>
										{{
											isPlanActive(plan)
												? 'Current Plan'
												: 'Select Plan'
										}}
									</b-button>
								</div>
							</template>
						</b-card-text>
					</b-card>
				</b-card-group>
			</b-col>
		</b-row>
		<!-- ASSISTANT CODE DELETED FOR THE TIME BEING. TO BE ADDED LATER -->
		<!-- <b-row
			class="mt-5"
			v-if="
				assistantPlan && featureEnabled('feature_assistant_management')
			"
		>
			<b-col>
				<b-card class="plan">
					<b-row no-gutters>
						<b-col sm="3">
							<b-card-body
								:title="assistantPlan.name"
								class="text-center"
							>
								<h1 class="price">
									${{ getPrice(assistantPlan) }}
								</h1>
								<b-button
									variant="primary"
									@click="startSubscription(assistantPlan)"
									:disabled="hasAssistantSubscription"
								>
									Add Assistant
								</b-button>
							</b-card-body>
						</b-col>
						<b-col sm="9">
							<b-card-body>
								<b-card-text class="features">
									Full access to collaborate on the files your
									lawyer has on the go.
								</b-card-text>
							</b-card-body>
						</b-col>
					</b-row>
				</b-card>
			</b-col>
		</b-row> -->
		<!-- <b-row class="mt-5 text-center">
			<b-col>
				<p>Questions? Divii experts are here to help.</p>
			</b-col>
		</b-row> -->
	</div>
</template>
<style>
/* Loading UI Styles */
.loading-container {
	padding: 4rem 1rem;
	min-height: 320px;
	display: flex;
	align-items: center;
	justify-content: center;
	animation: fadeIn 0.5s ease-in;
}

.loading-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
}

.spinner-wrapper {
	position: relative;
	width: 44px;
	height: 44px;
	margin-bottom: 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

.spinner-ring {
	width: 44px;
	height: 44px;
	border: 3px solid rgba(63, 103, 114, 0.15);
	border-top-color: #3F6772;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

.loading-title {
	color: #243B42;
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0;
}

.loading-subtitle {
	color: #5C7A82;
	font-size: 0.95rem;
	margin: 0;
	font-weight: 400;
}

.container-outer-party .spinner-ring,
.founding-plan-page-bg .spinner-ring {
	border-color: rgba(255, 255, 255, 0.2);
	border-top-color: #F7A91B;
}

.container-outer-party .loading-title,
.founding-plan-page-bg .loading-title {
	color: #ffffff;
}

.container-outer-party .loading-subtitle,
.founding-plan-page-bg .loading-subtitle {
	color: rgba(255, 255, 255, 0.8);
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}


.plan {
	color: #3F6772;
}
.plan.active-plan{
	border:4px solid #3F6772;
}

.price {
	color: #3F6772;
}

.features {
	min-width: 250px;
	color: #3F6772;
}

.features ul {
	list-style: none;
	margin-left: -40px;
}

.features ul li:before {
	content: '✓  ';
}

.features ul li.spacer-item:before {
	content: '';
}

.container-outer{
	background: #f7f5f5;
	padding-bottom: 2rem;
}

/* Separating Party Plans Styling */
.container-outer-party {
	background: #3F6772;
	padding: 3rem 2rem;
	min-height: 100vh;
}

.container-outer-party .party-plans-title {
	color: #fff;
	font-size: 2rem;
	font-weight: 700;
	margin-bottom: 0.5rem;
}

.container-outer-party .party-plans-subtitle {
	color: rgba(255, 255, 255, 0.85);
	font-size: 1.05rem;
	margin-bottom: 0;
}

@media (max-width: 767.98px), (max-height: 500px) {
	.container-outer-party .party-plans-title {
		font-size: 24px;
	}
	.container-outer-party .party-plans-subtitle {
		font-size: 15px;
	}
	.container-outer-party .plan-party .card-title {
		font-size: 20px;
	}
	.container-outer-party .price-party {
		height: 2.75rem;
		font-size: 32px;
	}
	.container-fluid.container-outer-party {
		padding-left: 16px !important;
		padding-right: 16px !important;
	}
	.container-outer-party .row {
		justify-content: center;
	}
	.container-outer-party .card-deck {
		flex-direction: column;
		margin-left: 0;
		margin-right: 0;
	}
	.container-outer-party .card-deck .card {
		flex: none;
		width: 100%;
		margin: 0 auto 28px;
	}
	.container-fluid.container-outer-party {
		padding-top: 24px;
	}
}

.plan-party {
	background: rgba(255, 255, 255, 0.07) !important;
	border: 1px solid rgba(255, 255, 255, 0.25) !important;
	border-radius: 12px;
	color: #ffffff !important;
	position: relative;
	display: flex;
	flex-direction: column;
	height: 100%;
	align-items: stretch;
}

.plan-party.most-popular {
	border-color: #F7A91B !important;
}

/* Ensure card-group deck aligns cards properly */
.b-card-group.deck {
	display: flex;
	align-items: stretch;
}

.b-card-group.deck .plan-party {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.b-card-group.deck .card {
	display: flex;
	flex-direction: column;
}

.plan-party .card-title {
	color: #ffffff !important;
	font-weight: bold;
	font-size: 1.5rem;
	margin-bottom: 1rem;
	min-height: 3rem;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	text-align: left;
	word-wrap: break-word;
	overflow-wrap: break-word;
	max-width: 100%;
}

.plan-description {
	font-size: 0.875rem;
	color: rgba(255, 255, 255, 0.65) !important;
	line-height: 1.5;
	text-align: left;
	min-height: 4.5rem;
	margin-bottom: 1.5rem;
	flex-shrink: 0;
}


.billing-period {
	color: rgba(255, 255, 255, 0.7);
	font-size: 0.875rem;
	margin-bottom: 1.5rem;
}

.features-party {
	color: #ffffff !important;
	min-width: 250px;
}

.features-party ul {
	list-style: none;
	margin-left: -40px;
	text-align: left;
}

.features-party ul li.feature-item-party {
	color: #ffffff;
	font-size: 0.9rem;
	padding-left: 0;
}

.features-party ul li.feature-item-party:before {
	content: '✓  ';
	color: #ffffff;
	font-weight: bold;
	margin-right: 0.5rem;
}

.btn-party {
	background-color: #F7A91B !important;
	border-color: #F7A91B !important;
	color: #000000 !important;
	font-weight: 400;
	padding: 0.75rem 2rem;
	border-radius: 0.5rem;
	margin-top: 1.5rem;
}

.btn-party:hover {
	background-color: #d8940f !important;
	border-color: #d8940f !important;
}

.btn-party:disabled {
	background-color: rgba(247, 169, 27, 0.5) !important;
	border-color: rgba(247, 169, 27, 0.5) !important;
}

.btn-current-plan {
	background-color: #6c757d !important;
	border-color: #6c757d !important;
	font-weight: 400 !important;
}

.button-container {
	margin-top: 1.5rem;
	width: 100%;
}

.price-button-wrapper {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	margin-top: 0;
}

.price-container-party {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
}

.extra-tag {
	display: inline-block;
	background-color: rgba(247, 169, 27, 0.2);
	color: #F7A91B;
	font-size: 0.625rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	padding: 0.25rem 0.75rem;
	border-radius: 0.25rem;
	margin-bottom: 0.5rem;
	line-height: 1.2;
}

.price-party {
	color: #ffffff !important;
	font-weight: bold;
	font-size: 2.5rem;
	margin: 0;
	padding: 0;
	line-height: 1.2;
	text-align: center;
	height: 3.5rem;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	flex-shrink: 0;
}

.button-container.after-price {
	margin-top: 1.5rem;
	margin-bottom: 1.5rem;
	display: flex;
	align-items: flex-start;
	padding-top: 0;
	width: 100%;
	height: 3.5rem;
	flex-shrink: 0;
}

/* Most Popular Badge */
.most-popular-badge {
	position: absolute;
	top: -12px;
	left: 20px;
	background-color: #F7A91B;
	color: #000000;
	padding: 0.25rem 1rem;
	border-radius: 0.5rem;
	font-size: 0.75rem;
	font-weight: 400;
	z-index: 10;
	overflow: hidden;
}

.most-popular-badge::before {
	content: '';
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(
		90deg,
		transparent,
		rgba(255, 255, 255, 0.4),
		transparent
	);
	animation: shineSweep 3s ease-in-out infinite;
}

@keyframes shineSweep {
	0% {
		left: -100%;
	}
	50%, 100% {
		left: 100%;
	}
}

/* Billing Toggle */
.billing-toggle {
	display: inline-flex;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 0.5rem;
	padding: 0.25rem;
	gap: 0;
}

.toggle-option {
	padding: 0.5rem 1.5rem;
	cursor: pointer;
	color: rgba(255, 255, 255, 0.7);
	font-weight: 500;
	border-radius: 0.375rem;
	transition: all 0.3s ease;
}

.toggle-option.active {
	background-color: #F7A91B;
	color: #000000;
	font-weight: 600;
}

.toggle-option:hover {
	color: #ffffff;
}

/* Upgrade alert for comprehensive plan when parenting plan is active */
.upgrade-alert-party {
	margin-top: 0.75rem;
	margin-bottom: 0.5rem;
	font-size: 0.875rem;
	line-height: 1.4;
}

/* Tooltip styling for upgrade card tooltip (Select Plan button) */
.tooltip.upgrade-card-tooltip-popup .tooltip-inner {
	background-color: #ffc107 !important;
	color: #000000 !important;
	font-size: 0.875rem !important;
	line-height: 1.4 !important;
	max-width: 400px !important;
	text-align: left !important;
	padding: 0.75rem !important;
}

.tooltip.upgrade-card-tooltip-popup.bs-tooltip-top .arrow::before {
	border-top-color: #ffc107 !important;
}

.tooltip.upgrade-card-tooltip-popup.bs-tooltip-bottom .arrow::before {
	border-bottom-color: #ffc107 !important;
}

.tooltip.upgrade-card-tooltip-popup.bs-tooltip-left .arrow::before {
	border-left-color: #ffc107 !important;
}

.tooltip.upgrade-card-tooltip-popup.bs-tooltip-right .arrow::before {
	border-right-color: #ffc107 !important;
}

/* New single "founding rate" lawyer plan (feature_lawyer_founding_plan).
   Scoped to its own founding-plan-* classes so it cannot affect the
   existing .plan/.price/.features rules used by the two-plan view above. */

/* Applied to the page's own root element only while showFoundingLawyerPlan is
   true (see the :class binding on the root div), so the old two-plan view's
   background is completely unaffected when the flag is off. */
.founding-plan-page-bg {
	background-color: #3f6772;
	min-height: 100vh;
}

.founding-plan-section {
	padding: 3rem 1.5rem 2.5rem;
	width: 100%;
}

.founding-plan-page {
	max-width: 860px;
	margin: 0 auto;
}

.founding-plan-cards-row {
	display: flex;
	align-items: stretch;
	gap: 1.75rem;
	flex-wrap: wrap;
	/* Reserves room for .founding-plan-footnote, which is absolutely
	   positioned just below the founding-rate card. */
	margin-bottom: 3rem;
}

.founding-plan-cards-row > .founding-plan-card,
.founding-plan-cards-row > .founding-plan-firm-card {
	flex: 1 1 360px;
}

.founding-plan-eyebrow {
	color: #b7c6c6;
	font-size: 0.8rem;
	margin-bottom: 0.75rem;
}

.founding-plan-headline {
	color: #ffffff;
	font-weight: 700;
	font-size: 1.75rem;
	line-height: 1.3;
	max-width: 520px;
	margin: 0 auto 1rem;
}

.founding-plan-subheadline {
	color: #cfdada;
	font-size: 0.95rem;
	max-width: 400px;
	margin: 0 auto 2rem;
}

.founding-plan-card {
	position: relative;
	display: flex;
	flex-direction: column;
	background-color: #ffffff;
	padding: 1.75rem;
	border-radius: 14px;
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
}

/* Both cards are stretched to equal height by .founding-plan-cards-row, and
   their CTA buttons should sit at the same bottom edge regardless of how
   much feature copy sits above them. */
.founding-plan-card .founding-plan-cta {
	margin-top: auto;
}

/* Firm Plan companion tile: reuses .founding-plan-card's rounded shape and
   sizing, but tinted so it reads as a distinct, complementary tile next to
   the white founding-rate card. */
.founding-plan-firm-card {
	position: relative;
	background-color: #fdf3e6;
	border: 1px solid #f5e2bf;
	box-shadow: none;
	overflow: hidden;
}

/* Decorative building glyph in the top-right corner, matching the reference. */
.founding-plan-firm-illustration {
	position: absolute;
	top: 1.25rem;
	right: 1.5rem;
	width: 56px;
	height: 56px;
	color: #f0dcae;
}

.founding-plan-firm-illustration svg {
	width: 100%;
	height: 100%;
}

.founding-plan-firm-badge {
	display: inline-block;
	align-self: flex-start;
	background-color: #ecdcb8;
	color: #7a6a4f;
	font-weight: 700;
	padding: 0.3rem 0.85rem;
	border-radius: 999px;
	font-size: 0.8rem;
	margin-bottom: 0.9rem;
}

.founding-plan-firm-title {
	color: #1c1f22;
	font-weight: 700;
	font-size: 2.25rem;
	margin-bottom: 1rem;
}

.founding-plan-firm-description {
	color: #495057;
	font-size: 0.95rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
}

.founding-plan-firm-features {
	list-style: none;
	padding-left: 0;
	margin: 0 0 1.5rem;
}

.founding-plan-firm-features li {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	margin-bottom: 1.1rem;
}

.founding-plan-firm-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	background-color: #f7e2bc;
	color: #b3721f;
}

.founding-plan-firm-icon svg {
	width: 16px;
	height: 16px;
}

.founding-plan-badge-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 0.9rem;
}

.founding-plan-badge {
	position: relative;
	display: inline-block;
	overflow: hidden;
	background-color: #f2a93d;
	color: #29323c;
	font-weight: 700;
	padding: 0.3rem 0.85rem;
	border-radius: 999px;
	font-size: 0.8rem;
}

/* Only applied when the badge reads "Founding rate" (see the :class binding
   in the template) — a "Current Plan" badge never sparkles. */
.founding-plan-badge-sparkle::after {
	content: '';
	position: absolute;
	top: 0;
	left: -60%;
	width: 45%;
	height: 100%;
	background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.85), transparent);
	animation: founding-plan-badge-sparkle-sweep 3.2s ease-in-out infinite;
}

@keyframes founding-plan-badge-sparkle-sweep {
	0% {
		left: -60%;
	}
	45% {
		left: 130%;
	}
	100% {
		left: 130%;
	}
}

@media (prefers-reduced-motion: reduce) {
	.founding-plan-badge-sparkle::after {
		animation: none;
		display: none;
	}
}

.founding-plan-list-price {
	color: #adb5bd;
	text-decoration: line-through;
	font-size: 0.9rem;
}

.founding-plan-price-block {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
}

.founding-plan-price-row {
	display: flex;
	align-items: baseline;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.founding-plan-price {
	color: #1c1f22;
	font-size: 2.25rem;
	font-weight: 700;
}

.founding-plan-price-suffix {
	color: #6c757d;
	font-size: 0.95rem;
}

.founding-plan-rate-note {
	color: #6c757d;
	font-size: 0.8rem;
	text-align: right;
	max-width: 45%;
	margin: 0;
}

.founding-plan-divider {
	border: none;
	border-top: 1px solid #e5e9ea;
	margin: 0 0 1.25rem;
}

.founding-plan-features {
	list-style: none;
	padding-left: 0;
	margin: 0 0 1.5rem;
}

.founding-plan-features li {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	color: #29323c;
	font-size: 0.92rem;
	margin-bottom: 1rem;
}

.founding-plan-check {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background-color: rgba(242, 169, 61, 0.15);
	color: #f2a93d;
	font-weight: 700;
	font-size: 0.75rem;
	margin-top: 0.1rem;
}

.founding-plan-feature-title {
	display: block;
	font-weight: 700;
	color: #1c1f22;
}

.founding-plan-feature-subtitle {
	display: block;
	color: #6c757d;
	font-size: 0.85rem;
	margin-top: 0.15rem;
}

.founding-plan-seats-row {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1.25rem;
}

.founding-plan-seats-label {
	color: #495057;
	font-size: 0.92rem;
}

.founding-plan-seats-input {
	max-width: 70px;
	text-align: center;
}

.founding-plan-seats-total {
	color: #1c1f22;
	font-weight: 700;
	margin-left: auto;
}

.founding-plan-cta.btn {
	background-color: #f2a93d;
	border-color: #f2a93d;
	color: #29323c;
	font-weight: 700;
	padding: 0.75rem 1rem;
}

.founding-plan-cta.btn:hover,
.founding-plan-cta.btn:focus,
.founding-plan-cta.btn:active {
	background-color: #e59c2e !important;
	border-color: #e59c2e !important;
	color: #29323c !important;
}

/* Firm Plan is sales-assisted, not a checkout — styled as a secondary/outline
   action so it doesn't compete visually with the founding-rate CTA. */
.founding-plan-cta-outline.btn {
	background-color: transparent;
	border-color: #d38a2e;
	color: #b3721f;
	box-shadow: none;
}

.founding-plan-cta-outline.btn:hover,
.founding-plan-cta-outline.btn:focus,
.founding-plan-cta-outline.btn:active {
	background-color: rgba(211, 138, 46, 0.1) !important;
	border-color: #d38a2e !important;
	color: #b3721f !important;
	box-shadow: none !important;
}

/* Sits just below the card, outside its white background (same pattern as
   .founding-plan-contact-note lower on the page), so the disclaimer text
   never pushes the CTA button out of alignment with the Firm Plan card's. */
.founding-plan-footnote {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	color: #cfdada;
	font-size: 0.8rem;
	text-align: center;
	margin-top: 0.9rem;
	margin-bottom: 0;
}

.founding-plan-contact-note {
	color: #cfdada;
	font-size: 0.85rem;
	margin-top: 1.75rem;
	margin-bottom: 0;
}

.founding-plan-link,
.founding-plan-link:hover {
	color: #f2a93d;
	cursor: pointer;
}

.founding-plan-link-underline {
	text-decoration: underline;
}

/* Firm Plan contact modal — mirrors the HubSpot hosted form page: teal banner
   title, light page background, form sitting in a white shadowed card. */
/* Opaque backdrop so the plan cards behind don't show around the modal. */
/* BootstrapVue only adds `.show` during the fade-in transition, so the opacity
   has to live on the base selector to persist once the modal is open. */
.firm-plan-contact-modal + .modal-backdrop {
	background-color: #213439;
	opacity: 1;
}

.firm-plan-contact-modal .modal-content {
	border: none;
	border-radius: 8px;
	overflow: hidden;
}

.firm-plan-contact-header {
	background: #3F6772;
	border-bottom: none;
	justify-content: center;
	position: relative;
	padding: 1.25rem 3rem;
}

.firm-plan-contact-header .modal-title {
	color: #ffffff;
	font-weight: 700;
	font-size: 1.75rem;
	text-align: center;
}

.firm-plan-contact-title-accent {
	color: #F7A91B;
}

.firm-plan-contact-header .close {
	color: #ffffff;
	opacity: 0.9;
	text-shadow: none;
	position: absolute;
	right: 1rem;
	top: 50%;
	transform: translateY(-50%);
	margin: 0;
	padding: 0;
}

.firm-plan-contact-body {
	background: #f4f7f9;
	padding: 1.5rem;
}

.firm-plan-contact-card {
	background: #ffffff;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
	max-width: 560px;
	margin: 0 auto;
	padding: 1.5rem 1.25rem;
	position: relative;
}

.firm-plan-contact-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 420px;
}

.firm-plan-contact-loading .spinner-ring {
	width: 48px;
	height: 48px;
	border-top-color: #3F6772;
}

/* Kept laid out (not display:none) while HubSpot renders so its iframe can
   measure its own height; just hidden and out of flow behind the spinner. */
.firm-plan-contact-form-pending {
	position: absolute;
	top: 1.5rem;
	left: 1.25rem;
	right: 1.25rem;
	visibility: hidden;
}

@media (max-width: 575.98px) {
	.firm-plan-contact-header .modal-title {
		font-size: 1.25rem;
	}

	.firm-plan-contact-body {
		padding: 1rem;
	}
}
</style>
